import React, { useState, useEffect } from "react";
import { axiosPrivate } from "../../../api/axios";

// Helper to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Helper to format OS name
const formatOS = (os) => {
  if (!os) return "Unknown";
  if (os.includes("darwin")) return "macOS";
  if (os.includes("win32") || os.includes("win")) return "Windows";
  if (os.includes("linux")) return "Linux";
  return os;
};

const DesktopStats = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axiosPrivate.post("/admin/desktop/stats"),
        axiosPrivate.post("/admin/desktop/users"),
      ]);
      setStats(statsResponse.data);
      setUsers(usersResponse.data.users || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading stats...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {error}
        <button
          onClick={fetchStats}
          className="ml-4 text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Users Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Active Desktop Users</h3>
        <div className="text-4xl font-bold text-blue-600">
          {stats?.activeUsers ?? 0}
        </div>
        <p className="text-sm text-gray-500 mt-1">in the last 30 minutes</p>
      </div>

      {/* Telemetry Stats Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Telemetry (Last 24h)</h3>
        {stats?.telemetryStats && Object.keys(stats.telemetryStats).length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(stats.telemetryStats).map(([eventType, count]) => (
              <div key={eventType} className="bg-gray-50 rounded p-3">
                <div className="text-sm text-gray-600 truncate" title={eventType}>
                  {eventType}
                </div>
                <div className="text-xl font-semibold">{count}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No telemetry data available</p>
        )}
      </div>

      {/* Current Config Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Current Configuration</h3>
        {stats?.config ? (
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Config Version</span>
              <span className="font-mono">{stats.config.version}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Beta End Date</span>
              <span className="font-mono">{stats.config.betaEndDate}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Min App Version</span>
              <span className="font-mono">{stats.config.minAppVersion}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Sync Interval</span>
              <span className="font-mono">{stats.config.syncIntervalMs}ms</span>
            </div>
            <div className="pt-2">
              <span className="text-gray-600 block mb-2">Feature Flags</span>
              <div className="flex flex-wrap gap-2">
                {stats.config.featureFlags &&
                  Object.entries(stats.config.featureFlags).map(([flag, enabled]) => (
                    <span
                      key={flag}
                      className={`px-2 py-1 rounded text-sm ${
                        enabled
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {flag}: {enabled ? "ON" : "OFF"}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">No config data available</p>
        )}
      </div>

      {/* Desktop Users List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Desktop App Users</h3>
        {users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OS
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr key={`${user.user_email}-${user.device_id || index}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.user_email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                      {user.app_version || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatOS(user.os)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatRelativeTime(user.last_seen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No users have logged in yet</p>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={fetchStats}
        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md"
      >
        Refresh Stats
      </button>
    </div>
  );
};

export default DesktopStats;
